import { useState } from "react";

import type { Campaign } from "../../api/generated";

/**
 * Placeholder id for a campaign staged via the picker's "+ Create" row but not
 * yet persisted. campaigns.id is a SERIAL starting at 1 (and 0 is the
 * "no campaign" filter sentinel), so a negative id can never collide with a
 * real one.
 */
export const PENDING_CAMPAIGN_ID = -1;

export const isPendingCampaign = (
  campaign: Campaign | null | undefined,
): campaign is Campaign => campaign?.id === PENDING_CAMPAIGN_ID;

const buildPendingCampaign = (name: string): Campaign => ({
  id: PENDING_CAMPAIGN_ID,
  name: name.trim(),
  isDefault: false,
  createdAt: "",
  updatedAt: "",
});

export type UsePendingCampaignParams = {
  campaigns: Campaign[];
  /** The form's current selection; the create error only shows while it's the staged campaign. */
  selected: Campaign | null | undefined;
  /** Persists the campaign. Only called from `resolve`, i.e. on the owning form's submit. */
  onCreateCampaign?: (name: string) => Promise<Campaign>;
  onCreateSuccess?: (campaign: Campaign) => void;
  /** The persisting call's error; only surfaced once a submit-time create has failed. */
  createError?: string;
};

export type UsePendingCampaignResult = {
  /** `campaigns` with the staged campaign (if any) prepended, so it stays selectable. */
  campaigns: Campaign[];
  /** Pass to `CampaignPicker.onCreate`: stages the campaign locally, no API call. */
  stage: (name: string) => Promise<Campaign>;
  /**
   * Call on submit, right before creating the link/banner. Persists a staged
   * campaign and returns the real one; returns any other selection unchanged.
   * Throws if persisting fails so the caller can abort the submit.
   */
  resolve: (
    selected: Campaign | null | undefined,
  ) => Promise<Campaign | undefined>;
  reset: () => void;
  createError?: string;
};

export const usePendingCampaign = ({
  campaigns,
  selected,
  onCreateCampaign,
  onCreateSuccess,
  createError,
}: UsePendingCampaignParams): UsePendingCampaignResult => {
  const [pending, setPending] = useState<Campaign | null>(null);
  const [hasCreateFailed, setHasCreateFailed] = useState(false);

  const stage = async (name: string) => {
    const staged = buildPendingCampaign(name);
    setPending(staged);
    setHasCreateFailed(false);
    return staged;
  };

  const resolve = async (campaign: Campaign | null | undefined) => {
    if (!campaign) return undefined;
    if (!isPendingCampaign(campaign) || !onCreateCampaign) return campaign;
    try {
      const created = await onCreateCampaign(campaign.name);
      // Persisted now — drop the placeholder so a retry after a failed
      // link/banner create reuses `created` instead of creating it twice.
      setPending(null);
      setHasCreateFailed(false);
      onCreateSuccess?.(created);
      return created;
    } catch (error) {
      setHasCreateFailed(true);
      throw error;
    }
  };

  const reset = () => {
    setPending(null);
    setHasCreateFailed(false);
  };

  return {
    campaigns: pending ? [pending, ...campaigns] : campaigns,
    stage,
    resolve,
    reset,
    createError:
      hasCreateFailed && isPendingCampaign(selected) ? createError : undefined,
  };
};
